import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

export type ToolbarPlacement = 'below' | 'above';

type DataAttrs = { [key: `data-${string}`]: string | undefined };

export interface ElementFrameProps {
  /** Stable id: `title`, `logo`, `design:<instanceId>`… read by the click-away logic and tests. */
  elementKey: string;
  selected: boolean;
  editable: boolean;
  onSelect: () => void;
  /** The X in the top-right corner: toggle off for set elements, remove for design elements. Omit for no X. */
  onDismiss?: () => void;
  dismissTitle?: string;
  /**
   * Toolbar for the selected element. Sits directly ABOVE the element,
   * left-aligned to its left edge, outside the outline, taking no layout
   * space and keeping its natural width however narrow the element is. Above,
   * because the one spacing control adjusts the space BELOW the element and
   * the toolbar must never sit in it; it falls back below only when the
   * canvas top leaves no room, and shifts sideways to stay inside the canvas.
   * A function receives the placement so the toolbar can open its popovers
   * away from the element.
   */
  toolbar?: React.ReactNode | ((placement: ToolbarPlacement) => React.ReactNode);
  /** Corner radius the outline follows (the category pill); square otherwise. */
  outlineRadius?: number;
  /** Gap between the element and its outline; the pill sits tighter (1 px). */
  outlineOffset?: number;
  /** The canvas the toolbar must stay inside of (defaults to the offset parent). */
  boundsRef?: React.RefObject<HTMLElement | null>;
  /** Host-rendered handles (resize, rotate, text width); shown only while selected. */
  handles?: React.ReactNode;
  /** Design elements keep their click inside the design layer. */
  stopClickPropagation?: boolean;
  className?: string;
  style?: React.CSSProperties;
  wrapperProps?: React.HTMLAttributes<HTMLDivElement> & DataAttrs;
  children: React.ReactNode;
}

/** Square corners, set-element colour: the one selection look for every element. */
export const FRAME_OUTLINE = '1px solid rgba(255,255,255,0.92)';
export const FRAME_OUTLINE_OFFSET = 2;
/** Flush against the outline: outline offset + the outline itself + 1. */
const TOOLBAR_GAP = FRAME_OUTLINE_OFFSET + 2;
const DISMISS_SIZE = 18;

/**
 * The selection chrome every overlay element gets, on the card and in the
 * template editors: outline, the X, handles and the toolbar slot. Hover does
 * nothing; the host decides `selected` (see useOverlaySelection).
 */
const ElementFrame: React.FC<ElementFrameProps> = ({
  elementKey,
  selected,
  editable,
  onSelect,
  onDismiss,
  dismissTitle = 'Remove',
  toolbar,
  outlineRadius,
  outlineOffset = FRAME_OUTLINE_OFFSET,
  boundsRef,
  handles,
  stopClickPropagation = false,
  className,
  style,
  wrapperProps,
  children,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState<ToolbarPlacement>('above');
  const [shiftX, setShiftX] = useState(0);
  const shiftRef = useRef(0);
  const measureRef = useRef<() => void>(() => {});

  const showChrome = selected && editable;
  const hasToolbar = showChrome && toolbar != null;

  // Keep the toolbar inside the canvas: below the element when it fits,
  // above when only that fits, nudged sideways when it would poke out.
  // Re-measured when the toolbar appears, when the element or the canvas
  // changes size (text grows, aspect switches) and on window resize; it only
  // writes state on a change.
  useLayoutEffect(() => {
    if (!hasToolbar) {
      if (shiftRef.current !== 0) {
        shiftRef.current = 0;
        setShiftX(0);
      }
      setPlacement((p) => (p === 'above' ? p : 'above'));
      return;
    }
    const measure = () => {
      const el = wrapperRef.current;
      const tb = toolbarRef.current;
      if (!el || !tb) return;
      const boundsEl = boundsRef?.current ?? (el.offsetParent as HTMLElement | null);
      if (!boundsEl) return;
      const b = boundsEl.getBoundingClientRect();
      const r = el.getBoundingClientRect();
      const t = tb.getBoundingClientRect();
      if (b.height === 0 || t.height === 0) return; // not laid out yet (or jsdom)

      const fitsBelow = r.bottom + TOOLBAR_GAP + t.height <= b.bottom;
      const fitsAbove = r.top - TOOLBAR_GAP - t.height >= b.top;
      const nextPlacement: ToolbarPlacement = fitsAbove || !fitsBelow ? 'above' : 'below';

      const rawLeft = t.left - shiftRef.current;
      const rawRight = rawLeft + t.width;
      let nextShift = 0;
      if (rawLeft < b.left) nextShift = b.left - rawLeft;
      else if (rawRight > b.right) nextShift = b.right - rawRight;
      nextShift = Math.round(nextShift);

      setPlacement((p) => (p === nextPlacement ? p : nextPlacement));
      if (nextShift !== shiftRef.current) {
        shiftRef.current = nextShift;
        setShiftX(nextShift);
      }
    };
    measureRef.current = measure;
    measure();
    window.addEventListener('resize', measure);
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => measure());
      if (wrapperRef.current) ro.observe(wrapperRef.current);
      if (toolbarRef.current) ro.observe(toolbarRef.current);
      const boundsEl = boundsRef?.current ?? (wrapperRef.current?.offsetParent as HTMLElement | null);
      if (boundsEl) ro.observe(boundsEl);
    }
    return () => {
      window.removeEventListener('resize', measure);
      ro?.disconnect();
    };
  }, [hasToolbar, boundsRef]);

  // When the frame and its canvas mount in the same commit, the layout effect
  // above runs before the canvas ref is attached (children first). Measure
  // once more after commit so the first placement is right without a resize.
  useEffect(() => {
    measureRef.current();
  }, [hasToolbar, boundsRef]);

  const { onClick: hostOnClick, ...restWrapperProps } = wrapperProps ?? {};

  // Every frame takes its own pointer events: the stack rows are
  // pointer-events:none so blank row space falls through to whatever is
  // under it (a design element behind the text, then the canvas), instead of
  // the row swallowing clicks across its whole width (Depth Ordering of
  // design element, 2026-10-08). A host may still override via `style`.
  const wrapperStyle: React.CSSProperties = {
    position: 'relative',
    pointerEvents: 'auto',
    ...style,
    ...(showChrome
      ? {
          outline: FRAME_OUTLINE,
          outlineOffset,
          zIndex: 50,
          ...(outlineRadius ? { borderRadius: outlineRadius } : {}),
        }
      : {}),
  };

  // `width: max-content` is load-bearing: an absolutely positioned box is
  // otherwise shrink-to-fit against its containing block (the element), so a
  // narrow pill or logo squeezed the toolbar and its controls collapsed onto
  // each other (punchlist 1–4).
  const toolbarStyle: React.CSSProperties = {
    position: 'absolute',
    ...(placement === 'below'
      ? { top: '100%', marginTop: TOOLBAR_GAP }
      : { bottom: '100%', marginBottom: TOOLBAR_GAP + (onDismiss ? DISMISS_SIZE / 2 : 0) }),
    left: 0,
    transform: shiftX ? `translateX(${shiftX}px)` : undefined,
    width: 'max-content',
    maxWidth: 'none',
    zIndex: 60,
    whiteSpace: 'nowrap',
    pointerEvents: 'auto',
  };

  return (
    <div
      {...restWrapperProps}
      ref={wrapperRef}
      data-overlay-element={elementKey}
      data-selected={showChrome ? 'true' : undefined}
      className={className}
      style={wrapperStyle}
      onClick={(e) => {
        hostOnClick?.(e);
        if (!editable) return;
        if (stopClickPropagation) e.stopPropagation();
        if (!selected) onSelect();
      }}
    >
      {children}
      {showChrome && handles}
      {showChrome && onDismiss && (
        <button
          type="button"
          data-overlay-dismiss=""
          title={dismissTitle}
          aria-label={dismissTitle}
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          style={{
            position: 'absolute',
            top: -(DISMISS_SIZE / 2) - FRAME_OUTLINE_OFFSET,
            right: -(DISMISS_SIZE / 2) - FRAME_OUTLINE_OFFSET,
            width: DISMISS_SIZE,
            height: DISMISS_SIZE,
            borderRadius: '50%',
            background: 'rgba(0,0,0,0.85)',
            color: 'white',
            border: '1px solid white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            pointerEvents: 'auto',
            padding: 0,
            zIndex: 61,
          }}
        >
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3}>
            <path d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      )}
      {hasToolbar && (
        // A press on the pill stays on the pill: the design element's wrapper
        // starts a drag on pointerdown, and a slider nudge must not move it.
        // Click-away listens on the document in capture, so it still sees it.
        <div ref={toolbarRef} data-overlay-toolbar="" data-placement={placement} style={toolbarStyle} onPointerDown={(e) => e.stopPropagation()}>
          {typeof toolbar === 'function' ? toolbar(placement) : toolbar}
        </div>
      )}
    </div>
  );
};

export default ElementFrame;
